import React from "react";
import ServicePage from "../components/ServicePage";

const Icon = ({ d, size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
  </svg>
);
const CarIcon      = (p) => <Icon {...p} d={["M5 17h14M5 17a2 2 0 002 2h0a2 2 0 002-2M5 17V9l2-4h10l2 4v8M17 17a2 2 0 002 2h0a2 2 0 002-2","M3 11h18"]} />;
const ShieldIcon    = (p) => <Icon {...p} d="M12 2l8 3.5v5.5c0 5-3.4 9-8 11-4.6-2-8-6-8-11V5.5z" />;
const ClockIcon     = (p) => <Icon {...p} d={["M12 22a10 10 0 100-20 10 10 0 000 20z","M12 6v6l4 2"]} />;
const RouteIcon     = (p) => <Icon {...p} d={["M4 19a2.5 2.5 0 010-5h1a2.5 2.5 0 000-5H4","M20 5a2.5 2.5 0 010 5h-1a2.5 2.5 0 000 5h1"]} />;
const CheckIcon     = (p) => <Icon {...p} d="M20 6L9 17l-5-5" />;

const C = { green: "#006B3C", greenDk: "#004D2B", greenLt: "#E8F5EE", gold: "#B8933A", grayBd: "#E0E0E0", text: "#1A1A1A", textSub: "#666666" };

// Documents à fournir pour le chargement d'un véhicule - tel qu'exigé par
// la Direction Commerciale SETRAG (note d'information officielle).
const REQUIRED_DOCS = [
  "01 demande de réservation de wagon",
  "01 copie de la carte d'assurance (en cours de validité)",
  "01 copie de la carte grise",
  "01 copie de la pièce d'identité du convoyeur (en cours de validité)",
  "02 sangles de 5 tonnes ayant 02 crochets",
  "01 convoyeur obligatoire",
];

// Grille tarifaire officielle "Véhicules routiers" (poids ≤ 2,5 T),
// au départ d'Owendo-Quai.
const PRICING = [
  { gare: "Ntoum",              ht: 79593,  ttc: 94800  },
  { gare: "Ndjolé",              ht: 134286, ttc: 159800 },
  { gare: "Ndjolé / Débarcadère",ht: 136134, ttc: 162000 },
  { gare: "Lopé",                ht: 173826, ttc: 206900 },
  { gare: "Offoué",              ht: 182328, ttc: 217000 },
  { gare: "Booué",               ht: 192306, ttc: 228900 },
  { gare: "Lastourville",        ht: 245888, ttc: 292700 },
  { gare: "Moanda",              ht: 291340, ttc: 346700 },
  { gare: "Franceville",         ht: 306123, ttc: 364300 },
];

const fmt = (n) => n.toLocaleString("fr-FR");

export default function Vehicules() {
  return (
    <ServicePage
      title="Transport de véhicules"
      subtitle="Faites voyager votre voiture par train, en toute sécurité, sur les grands axes desservis par SETRAG."
      subject="Demande de transport de véhicule"
      formIntro="Indiquez ci-dessous les informations de votre véhicule et le trajet souhaité - un agent SETRAG vous recontactera pour finaliser les modalités."
      routeLabel="Trajet souhaité (ex : Owendo → Franceville)"
      features={[
        [CarIcon, "Tous types de véhicules", "Berlines, 4x4, utilitaires légers - selon les gabarits acceptés sur nos wagons porte-autos."],
        [ShieldIcon, "Sécurité garantie", "Votre véhicule voyage arrimé et sous surveillance pendant tout le trajet."],
        [ClockIcon, "Gain de temps", "Évitez les longues heures de route, surtout en saison des pluies."],
        [RouteIcon, "Sur les grands axes", "Libreville / Owendo ↔ Booué ↔ Franceville, aux mêmes horaires que nos trains voyageurs."],
      ]}
    >
      {/* DOCUMENTS À FOURNIR */}
      <div style={{ maxWidth: 900, margin: "32px auto 0", padding: "0 16px" }}>
        <div style={{ background: "#fff", border: `1px solid ${C.grayBd}`, borderRadius: 16, padding: 28 }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, margin: "0 0 4px", color: C.greenDk }}>Documents à fournir</h2>
          <p style={{ fontSize: 13, color: C.textSub, margin: "0 0 18px" }}>
            Pour le chargement de votre (vos) véhicule(s), merci de vous munir des éléments suivants :
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: 10 }}>
            {REQUIRED_DOCS.map((doc) => (
              <div key={doc} style={{ display: "flex", alignItems: "flex-start", gap: 10, background: C.greenLt, borderRadius: 10, padding: "10px 14px" }}>
                <span style={{ color: C.green, flexShrink: 0, marginTop: 1 }}><CheckIcon size={16} /></span>
                <span style={{ fontSize: 13, color: C.text, lineHeight: 1.5 }}>{doc}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* GRILLE TARIFAIRE */}
      <div style={{ maxWidth: 900, margin: "24px auto 0", padding: "0 16px" }}>
        <div style={{ background: "#fff", border: `1px solid ${C.grayBd}`, borderRadius: 16, padding: 28, overflowX: "auto" }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, margin: "0 0 4px", color: C.greenDk }}>Grille tarifaire - Véhicules routiers</h2>
          <p style={{ fontSize: 13, color: C.textSub, margin: "0 0 18px" }}>
            Tarifs au départ d'Owendo-Quai, pour un véhicule de poids ≤ 2,5 tonnes. Pour un véhicule plus lourd, contactez-nous via le formulaire ci-dessous.
          </p>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 480 }}>
            <thead>
              <tr style={{ background: C.greenDk, color: "#fff" }}>
                <th style={{ textAlign: "left", padding: "10px 14px", borderRadius: "8px 0 0 8px" }}>Gare</th>
                <th style={{ textAlign: "right", padding: "10px 14px" }}>Prix HT</th>
                <th style={{ textAlign: "right", padding: "10px 14px", borderRadius: "0 8px 8px 0" }}>Prix TTC</th>
              </tr>
            </thead>
            <tbody>
              {PRICING.map((row, i) => (
                <tr key={row.gare} style={{ background: i % 2 === 0 ? "#fff" : C.greenLt }}>
                  <td style={{ padding: "9px 14px", fontWeight: 600, color: C.text }}>{row.gare}</td>
                  <td style={{ padding: "9px 14px", textAlign: "right", color: C.textSub }}>{fmt(row.ht)} FCFA</td>
                  <td style={{ padding: "9px 14px", textAlign: "right", fontWeight: 700, color: C.green }}>{fmt(row.ttc)} FCFA</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p style={{ fontSize: 11, color: "#999", marginTop: 14 }}>
            HT = hors taxes · TTC = toutes taxes comprises. Tarifs susceptibles d'évoluer - confirmés lors de votre demande de réservation de wagon.
          </p>
        </div>
      </div>
    </ServicePage>
  );
}
